import Vue from 'vue';
import values from 'lodash/values';
import { fetchFeatures, fetchFeature, updateFeature, createFeature, deleteFeature } from './features.utils';
import { createFrozenMap, appendKindIfNotProvided } from '@/vuex/utils';
import { RECENTLY_SELECTED_HISTORY_TYPES } from '@/vuex/modules/user-settings/history/history.constants';

let loadAllPromise;

const features = {
    namespaced: true,
    state: {
        error: {},
        fetching: false,
        map: {},
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setMapAtKey (state, { id, feature }) {
            Vue.set(state.map, id, feature);
        },
        deleteMapKey (state, { id }) {
            Vue.delete(state.map, id);
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
    },
    actions: {
        async loadAll ({ commit, rootGetters }, { reFetch = false } = {}) {
            if (loadAllPromise && reFetch !== true) {
                await loadAllPromise;

                return;
            }

            const isSuperUser = rootGetters['auth/isSuper'];

            commit('setFetching', { fetching: true });
            loadAllPromise = fetchFeatures({ isSuperUser });

            try {
                const { data: features } = await loadAllPromise;
                const featuresWithKind = features.map((feature) => appendKindIfNotProvided(feature, 'Feature'));

                commit('setFetching', { fetching: false });
                commit('setMap', {
                    map: createFrozenMap(featuresWithKind),
                });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        loadOne ({ commit, getters }, { reFetch = false, ...payload }) {
            const { id } = payload;
            if (!reFetch && getters.featureById(id)) return Promise.resolve();

            commit('setFetching', { fetching: true });

            return fetchFeature(id)
                .then(({ data: feature }) => {
                    appendKindIfNotProvided(feature, 'Feature');

                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        id: feature.id,
                        feature: Object.freeze(feature),
                    });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        create ({ commit }, { shouldStartProcessing = true, ...payload } = {}) {
            commit('setFetching', { fetching: true });

            return createFeature(payload, shouldStartProcessing)
                .then(({ data: feature }) => {
                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        id: feature.id,
                        feature: Object.freeze(feature),
                    });

                    return feature;
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        update ({ commit }, { shouldStartProcessing = false, ...payload } = {}) {
            commit('setFetching', { fetching: true });

            return updateFeature(payload, shouldStartProcessing)
                .then(({ data: feature }) => {
                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        id: feature.id,
                        feature: Object.freeze(feature),
                    });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        remove ({ commit }, { id }) {
            commit('setFetching', { fetching: true });

            return deleteFeature(id)
                .then(() => {
                    commit('setFetching', { fetching: false });
                    commit('deleteMapKey', { id });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
    },
    getters: {
        list: (state) => values(state.map),
        featureById: (state) => (id) => state.map[id],
        featuresByAppId: (state, getters) => (appId) => getters.list.filter((feature) => feature.appId === appId),
        analyticsFeaturesListWithStars: (state, getters, _rootState, rootGetters) => {
            const isStarred = rootGetters['stars/getStarByItemId'];

            return getters.list.map((feature) =>
                Object.freeze({
                    id: feature.id,
                    name: feature.name,
                    group: feature.group,
                    appId: feature.appId,
                    appIcon: rootGetters['apps/getIconByAppId'](feature.appId),
                    kind: feature.kind,
                    dirty: feature.dirty,
                    isCoreEvent: feature.isCoreEvent,
                    isStarred: !!isStarred(feature.id),
                    icon: feature.icon || { type: 'feature' },
                }),
            );
        },
        recentFeaturesInReporting: (state, _getters, _rootState, rootGetters) => {
            const isStarred = rootGetters['stars/getStarByItemId'];

            return rootGetters['userSettings/history/itemHistory'](
                RECENTLY_SELECTED_HISTORY_TYPES.FEATURE_RECENTLY_SELECTED,
            )
                .filter(({ id }) => state.map[id])
                .map(({ id, timestamp }) => {
                    const feature = state.map[id];
                    const app = rootGetters['apps/getAppById'](feature.appId);

                    return {
                        id: feature.id,
                        name: feature.name,
                        group: feature.group,
                        appId: feature.appId,
                        appIcon: rootGetters['apps/getIconByAppId'](feature.appId),
                        appDisplayName: app ? app.displayName : undefined,
                        kind: feature.kind,
                        dirty: feature.dirty,
                        isCoreEvent: feature.isCoreEvent,
                        isStarred: !!isStarred(feature.id),
                        icon: feature.icon || { type: 'feature' },
                        lastUsed: timestamp,
                    };
                });
        },
    },
};

export default features;

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}
